import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Loader2, Search, Trash2, UserPlus, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PageHead, Panel } from "@/components/bits";
import { publicApi } from "@/services/api-client";
import { BusinessGate } from "@/features/business/business-gate";
import { useI18n } from "@/i18n/i18n-context";
import {
  ConfirmDialog,
  Empty,
  ErrorBox,
  KV,
  Pager,
  SkeletonRows,
  Tag,
  buildQuery,
  pageInfo,
  record,
  rows,
  text,
  useServer,
  type ApiRecord,
} from "@/features/finance/kit";

const PER_PAGE = 25;

export default function TeamPage() {
  const { t } = useI18n();
  return (
    <BusinessGate title={t("team.title")} what={t("team.title")}>
      <Team />
    </BusinessGate>
  );
}

const roleCode = (r: ApiRecord) => text(r, "code", "name", "role", "slug");

function Team() {
  const { t } = useI18n();
  const summary = useServer<unknown>("/team");
  const rolesRes = useServer<unknown>("/team/roles");
  const roles = useMemo(() => {
    const data = rolesRes.data;
    const value = Array.isArray(data)
      ? data
      : (record(data).roles ??
        record(data).assignable_roles ??
        record(data).data);
    return Array.isArray(value)
      ? value.map((role) =>
          typeof role === "string" ? { code: role } : record(role),
        )
      : rows(data);
  }, [rolesRes.data]);
  const roleCodes = roles.map(roleCode).filter(Boolean);
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  useEffect(() => {
    const t = setTimeout(() => setSearch(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => setPage(1), [search, status]);
  const members = useServer<unknown>(
    buildQuery("/team/members", { search, status, per_page: PER_PAGE, page }),
  );
  const list = useMemo(() => rows(members.data), [members.data]);
  const info = pageInfo(members.data, members.meta, list.length, PER_PAGE);
  const [invite, setInvite] = useState(false);
  const [edit, setEdit] = useState<ApiRecord | null>(null);
  const [remove, setRemove] = useState<ApiRecord | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);

  const sum = record(summary.data);
  const current = record(sum.current_membership ?? sum.membership);
  const activeOwners = list.filter(
    (m) =>
      text(m, "role", "role.code").toUpperCase() === "OWNER" &&
      text(m, "status").toUpperCase() === "ACTIVE",
  );
  const ownerCountKnown =
    info.last === 1 || info.last === null ? activeOwners.length : null;
  const isLastOwner = (m: ApiRecord) =>
    text(m, "role", "role.code").toUpperCase() === "OWNER" &&
    text(m, "status").toUpperCase() === "ACTIVE" &&
    ownerCountKnown !== null &&
    ownerCountKnown <= 1;

  const refresh = () => {
    members.reload();
    summary.reload();
  };

  async function doRemove() {
    if (!remove) return;
    setBusy(true);
    setErr(null);
    try {
      await publicApi(
        `/team/members/${encodeURIComponent(text(remove, "id"))}`,
        { method: "DELETE" },
      );
      setRemove(null);
      refresh();
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHead title={t("team.title")} sub={t("team.pageSub")}>
        <Button
          onClick={() => setInvite(true)}
          disabled={!roleCodes.length}
          data-testid="button-invite-member"
          className="gap-1.5"
        >
          <UserPlus className="h-4 w-4" /> {t("team.inviteBtn")}
        </Button>
      </PageHead>
      <div className="mb-5 grid gap-4 ">
        <Panel title={t("team.assignableRolesTitle")}>
          {rolesRes.error ? (
            <ErrorBox error={rolesRes.error} onRetry={rolesRes.reload} />
          ) : rolesRes.loading ? (
            <SkeletonRows n={2} />
          ) : (
            <ul className="space-y-2">
              {roles.map((r) => (
                <li key={roleCode(r)} className="rounded-lg border px-3 py-2">
                  <p className="text-sm font-bold">
                    {text(r, "label", "display_name") || roleCode(r)}{" "}
                    <span className="font-mono text-[11px] text-muted-foreground">
                      {roleCode(r)}
                    </span>
                  </p>
                  {Array.isArray(r.capabilities) && (
                    <p className="mt-0.5 text-[11px] text-muted-foreground">
                      {(r.capabilities as unknown[]).map(String).join(", ")}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
      <div className="mb-3 flex flex-col gap-2 sm:flex-row">
        <label className="relative flex-1">
          <span className="sr-only">{t("team.searchPlaceholder")}</span>
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("team.searchPlaceholder")}
            className="h-9 bg-card pl-9"
            data-testid="input-member-search"
          />
        </label>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger
            className="h-9 w-full bg-card sm:w-44"
            aria-label="Statut"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("team.allStatuses")}</SelectItem>
            {["ACTIVE", "PENDING", "SUSPENDED"].map((s) => (
              <SelectItem key={s} value={s}>
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="rise overflow-hidden rounded-xl border bg-card">
        {members.error ? (
          <ErrorBox
            className="m-4"
            error={members.error}
            onRetry={members.reload}
          />
        ) : members.loading && !list.length ? (
          <SkeletonRows />
        ) : list.length === 0 ? (
          <Empty
            icon={<Users className="h-5 w-5" />}
            title="Aucun membre trouvé"
          >
            Invitez un collaborateur avec un rôle adapté.
          </Empty>
        ) : (
          <ul className="divide-y">
            {list.map((m) => (
              <li
                key={text(m, "id")}
                className="flex flex-wrap items-center gap-3 px-4 py-3"
                data-testid={`row-member-${text(m, "id")}`}
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                  {(
                    text(m, "name", "user.name") ||
                    text(m, "email", "user.email") ||
                    "?"
                  )
                    .slice(0, 2)
                    .toUpperCase()}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold">
                    {text(m, "name", "user.name") || "—"}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">
                    {text(m, "email", "user.email")}
                  </p>
                </div>
                <Tag value={text(m, "role", "role.code")} />
                <Tag value={text(m, "status")} />
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8"
                    onClick={() => setEdit(m)}
                    disabled={!roleCodes.length}
                  >
                    Modifier
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 text-destructive"
                    aria-label="Retirer"
                    disabled={isLastOwner(m)}
                    title={
                      isLastOwner(m) ? "Dernier propriétaire actif" : undefined
                    }
                    onClick={() => {
                      setErr(null);
                      setRemove(m);
                    }}
                    data-testid="button-remove-member"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
        {(list.length > 0 || page > 1) && (
          <Pager info={info} loading={members.loading} onPage={setPage} />
        )}
      </div>
      <InviteDialog
        open={invite}
        roles={roleCodes}
        onClose={() => setInvite(false)}
        onDone={refresh}
      />
      <EditDialog
        member={edit}
        roles={roleCodes}
        lastOwner={edit ? isLastOwner(edit) : false}
        onClose={() => setEdit(null)}
        onDone={refresh}
      />
      <ConfirmDialog
        open={Boolean(remove)}
        destructive
        title="Retirer ce membre"
        description={`${text(remove, "email", "user.email")} perdra l'accès à cet espace entreprise.`}
        confirmLabel="Retirer"
        busy={busy}
        error={err}
        onClose={() => setRemove(null)}
        onConfirm={() => void doRemove()}
      />
    </>
  );
}

function InviteDialog({
  open,
  roles,
  onClose,
  onDone,
}: {
  open: boolean;
  roles: string[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  useEffect(() => {
    if (open) {
      setEmail("");
      setName("");
      setRole(roles.find((r) => r !== "OWNER") ?? roles[0] ?? "");
      setErr(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const valid =
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) &&
    roles.includes(role) &&
    name.trim().length > 1;
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!valid) return;
    setBusy(true);
    setErr(null);
    try {
      await publicApi("/team/members", {
        method: "POST",
        body: { email: email.trim(), role, name: name.trim() },
      });
      onDone();
      onClose();
    } catch (x) {
      setErr(x);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="sm:max-w-[440px]">
        <form onSubmit={submit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>Inviter un membre</DialogTitle>
            <DialogDescription>
              Le rôle doit faire partie des rôles assignables retournés par le
              serveur.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-1.5">
            <Label htmlFor="inv-name">Nom</Label>
            <Input
              id="inv-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="inv-email">E-mail</Label>
            <Input
              id="inv-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="input-invite-email"
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="inv-role">Rôle</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger id="inv-role">
                <SelectValue placeholder="Choisir" />
              </SelectTrigger>
              <SelectContent>
                {roles.map((r) => (
                  <SelectItem key={r} value={r}>
                    {r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <ErrorBox error={err} />
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={busy}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={!valid || busy}
              data-testid="button-submit-invite"
            >
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Envoyer
              l'invitation
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditDialog({
  member,
  roles,
  lastOwner,
  onClose,
  onDone,
}: {
  member: ApiRecord | null;
  roles: string[];
  lastOwner: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const [role, setRole] = useState("");
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  useEffect(() => {
    if (member) {
      setRole(text(member, "role", "role.code"));
      setStatus(text(member, "status"));
      setErr(null);
    }
  }, [member]);
  const demotesOwner = lastOwner && (role !== "OWNER" || status !== "ACTIVE");
  async function submit() {
    if (!member) return;
    setBusy(true);
    setErr(null);
    try {
      await publicApi(
        `/team/members/${encodeURIComponent(text(member, "id"))}`,
        { method: "PATCH", body: { role, status } },
      );
      onDone();
      onClose();
    } catch (x) {
      setErr(x);
    } finally {
      setBusy(false);
    }
  }
  return (
    <ConfirmDialog
      open={Boolean(member)}
      title="Modifier le membre"
      description={text(member, "email", "user.email")}
      confirmLabel="Enregistrer"
      busy={busy}
      error={err}
      onClose={onClose}
      onConfirm={() => {
        if (!demotesOwner && roles.includes(role)) void submit();
      }}
    >
      <div className="grid gap-3">
        <div className="grid gap-1.5">
          <Label htmlFor="edit-role">Rôle</Label>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger id="edit-role">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {roles.map((r) => (
                <SelectItem key={r} value={r}>
                  {r}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="edit-status">Statut</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger id="edit-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {["ACTIVE", "SUSPENDED"].map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {demotesOwner && (
          <p className="text-xs text-destructive">
            Ce membre est le dernier propriétaire actif : il ne peut pas être
            rétrogradé ni suspendu.
          </p>
        )}
      </div>
    </ConfirmDialog>
  );
}
